import { PATH } from 'common/constants';
import { FeatureFlag, PermissionCode } from 'types';
import { useHasPermissions } from 'hooks/useHasPermissions';
import { useFeatureFlag } from 'hooks/featureFlags/useFeatureFlag';
import { isAmsAuthActive } from 'service/amsSession';
import { useWorkspaceMode } from 'components/AdminSettings/useAccountDetail';
import { useHasGrants } from 'components/AdminSettings/useHasGrants';
import { GRANT_ROLE_VIEW, GRANT_USER_VIEW, GRANT_WORKSPACE_VIEW } from 'components/AdminSettings/grants';
import {useMemo} from "react";

export const useNavigationConfig = () => {
  const quickSightAuthoringEnabled = useFeatureFlag(FeatureFlag.QUICKSIGHT_AUTHORING_ENABLED);
  const dataManagementEnabled = useFeatureFlag(FeatureFlag.DATA_MANAGEMENT);
  const isPayTypeGradeEnabled = useFeatureFlag(FeatureFlag.PAY_TYPE_GRADE_ENABLED);
  const blueprintsEnabled = useFeatureFlag(FeatureFlag.UI_BLUEPRINTS);

  // Market Price
  const { permitted: canViewMarketPricing } = useHasPermissions({ permissions: [PermissionCode.VIEW_MARKET_PRICING] })

  // Analyze
  const { permitted: canViewDataModels } = useHasPermissions({ permissions: [PermissionCode.VIEW_DATA_MODELS] })
  const { permitted: canViewAnalyzeDashboards } = useHasPermissions({ permissions: [PermissionCode.VIEW_ANALYZE_DASHBOARDS] })
  const { permitted: canManageReports } = useHasPermissions({ permissions: [PermissionCode.MANAGE_REPORTS] })

  // My Data
  const { permitted: canManageEmployees } = useHasPermissions({ permissions: [PermissionCode.MANAGE_EMPLOYEES] });
  const { permitted: canManageCompanyJobs } = useHasPermissions({ permissions: [PermissionCode.MANAGE_COMPANY_JOBS] });
  const { permitted: canManageSurveys } = useHasPermissions({ permissions: [PermissionCode.MANAGE_SURVEYS] });
  const { permitted: canManageCurrencies } = useHasPermissions({ permissions: [PermissionCode.MANAGE_CURRENCIES] });
  const canManageDataManagement = dataManagementEnabled;
  const canManageCompanyJobRewrites = canManageCompanyJobs && !isPayTypeGradeEnabled;


  const isMyDataMenuDisplayed = [
    canManageEmployees,
    canManageCompanyJobs,
    canManageSurveys,
    canManageCurrencies,
    canManageDataManagement,
  ].some(can => can)

  // Settings
  const { permitted: canManagePlans } = useHasPermissions({ permissions: [PermissionCode.MANAGE_PLANS] });
  const { permitted: canManagePaymarkets } = useHasPermissions({ permissions: [PermissionCode.MANAGE_PAYMARKETS] });
  const { permitted: canManageDataCutPaymarketMapping } = useHasPermissions({ permissions: [PermissionCode.MANAGE_DATA_CUT_PAYMARKET_MAPPINGS] });
  const { permitted: canManageLevelMappings } = useHasPermissions({ permissions: [PermissionCode.MANAGE_LEVEL_MAPPINGS] });
  const { permitted: canManageTags } = useHasPermissions({ permissions: [PermissionCode.MANAGE_TAGS] });
  const { permitted: canManageAging } = useHasPermissions({ permissions: [PermissionCode.MANAGE_AGING] });
  const { permitted: canManagePricingPolicies } = useHasPermissions({ permissions: [PermissionCode.MANAGE_PRICING_POLICIES] })
  const { permitted: canManageTargetPercentiles } = useHasPermissions({ permissions: [PermissionCode.MANAGE_TARGET_PERCENTILES] })
  const { permitted: canManageCustomPercentiles } = useHasPermissions({ permissions: [PermissionCode.MANAGE_CUSTOM_PERCENTILES] })
  const { permitted: canViewUsers } = useHasPermissions({ permissions: [PermissionCode.VIEW_USERS] });
  const { permitted: canManageRoles } = useHasPermissions({ permissions: [PermissionCode.MANAGE_ROLES] });
  const { permitted: canManageDataAccessGroups } = useHasPermissions({ permissions: [PermissionCode.MANAGE_DATA_ACCESS_GROUPS] });

  const { permitted: canManageApiSetting } = useHasPermissions({ permissions: [PermissionCode.MANAGE_API_SETTINGS] })
  const { permitted: canManageSSOConfigurationPermission } = useHasPermissions({ permissions: [PermissionCode.MANAGE_IDP] });
  const { permitted: canManageSecurityEventApiPage } = useHasPermissions({ permissions: [PermissionCode.MANAGE_SECURITY_EVENT_API] });
  const { permitted: canManageAiFeatures } = useHasPermissions({ permissions: [PermissionCode.MANAGE_AI_FEATURES] });

  const amsAuthActive = isAmsAuthActive();
  const workspaceMode = useWorkspaceMode();
  const { permitted: canViewWorkspaces } = useHasGrants({ grants: [GRANT_WORKSPACE_VIEW] });
  const showWorkspaces = amsAuthActive && workspaceMode === 'multi' && canViewWorkspaces;
  // On an AMS session the Users/Roles items lead to the AMS pages and are gated on the grants those
  // pages' list endpoints enforce; on the legacy path they keep their MP permissions and pages.
  const { permitted: hasUserView } = useHasGrants({ grants: [GRANT_USER_VIEW] });
  const { permitted: hasRoleView } = useHasGrants({ grants: [GRANT_ROLE_VIEW] });
  const showUsers = amsAuthActive ? hasUserView : canViewUsers;
  const showRoles = amsAuthActive ? hasRoleView : canManageRoles;

  const isSettingsMenuDisplayed = [
    canManagePlans,
    canManagePaymarkets,
    canManageDataCutPaymarketMapping,
    canManageLevelMappings,
    canManageTags,
    canManageAging,
    canManagePricingPolicies,
    showUsers,
    showRoles,
    canManageApiSetting,
    canManageCompanyJobs,
    canManageTargetPercentiles,
    canManageCustomPercentiles,
    showWorkspaces,
  ].some(can => can)

  const isTechSettingsMenuDisplayed = canManageApiSetting || canManageSSOConfigurationPermission || canManageSecurityEventApiPage || canManageAiFeatures;

  const techSettingsPagePath =
    (canManageApiSetting && PATH.SETTINGS_HRIS)
    || (canManageSSOConfigurationPermission && PATH.SETTINGS_SSO_CONFIGURATION)
    || (canManageSecurityEventApiPage && PATH.SECURITY_EVENT_API)
    || (canManageAiFeatures && PATH.SETTINGS_AI_FEATURES);

  return useMemo(
    () => (
      (
        [
          {
            label: 'Home',
            key: PATH.DEFAULT,
          },
          canViewMarketPricing ? {
            label: 'Market Price',
            key: 'market-price-menu',
            children: [
              {
                label: 'Jobs',
                key: PATH.TABLE_JOB_MAPPING,
              },
              {
                label: 'Cuts',
                key: PATH.TABLE_PRICEJOB_MASS,
              },
              {
                label: 'Composites',
                key: PATH.TABLE_PRICEJOB(),
              },
            ],
          } : null,
          {
            label: 'Analyze',
            key: 'analyze-menu',
            children: [
              canViewAnalyzeDashboards ? {
                label: 'Dashboards',
                key: PATH.QUICKSIGHT,
              } : null,
              canManageReports ? {
                label: 'Tabular Reporting',
                key: PATH.REPORT,
              } : null,
              canViewDataModels ? {
                label: 'Range Models',
                key: PATH.MODEL,
              } : null,
              {
                label: 'Downloads',
                key: PATH.DOWNLOADS,
              },
              canViewAnalyzeDashboards && quickSightAuthoringEnabled ? {
                label: 'Authoring',
                key: PATH.QUICKSIGHT_AUTHORING,
              } : null,
            ],
          },
          isMyDataMenuDisplayed ? {
            label: 'My Data',
            key: 'my-data',
            children: [
              canManageEmployees ? {
                label: 'Employees',
                key: PATH.TABLE_EMPLOYEES,
              } : null,
              canManageCompanyJobs ? {
                label: 'Jobs',
                key: PATH.COMPANY_JOBS,
              } : null,
              canManageSurveys ? {
                label: 'Surveys',
                key: PATH.TABLE_SURVEYS,
              } : null,
              canManageSurveys ? {
                label: 'Participation',
                key: PATH.TABLE_PARTICIPATION,
                'data-testid': 'participationMenuItem',
              } : null,
              canManageCurrencies ? {
                label: 'Currencies',
                key: PATH.TABLE_CURRENCY,
              } : null,
              {
                label: 'Ranges',
                key: PATH.RANGES,
                'data-testid': 'rangesMenuItem',
              },
              canManageDataManagement ? {
                label: 'HRIS Data Management',
                key: PATH.DATA_MANAGEMENT,
              } : null,
            ]
          } : null,
          isSettingsMenuDisplayed ? {
            label: 'Settings',
            key: 'application-settings',
            children: [
              canManagePlans ? {
                label: 'Plans',
                key: PATH.TABLE_PLANS,
              } : null,
              canManagePaymarkets ? {
                label: 'PayMarkets',
                key: PATH.TABLE_PAYMARKET,
              } : null,
              canManageDataCutPaymarketMapping ? {
                label: 'PayMarket/Cut Mapping',
                key: PATH.TABLE_PAYMARKET_MAPPING,
              } : null,
              canManageLevelMappings ? {
                label: 'Level Mapping',
                key: PATH.TABLE_LEVEL_MAPPING,
              } : null,
              canManagePricingPolicies ? {
                label: 'Policies',
                key: PATH.TABLE_POLICIES,
              } : null,
              canManageTargetPercentiles || canManageCustomPercentiles ? {
                label: 'Percentiles + Custom Percentiles',
                key: canManageTargetPercentiles ? PATH.PERCENTILES : PATH.CUSTOM_PERCENTILES,
              } : null,
              showUsers ? {
                label: 'Users',
                key: amsAuthActive ? PATH.ADMIN_USERS : PATH.TABLE_USERS(),
              } : null,
              showRoles ? {
                label: 'Roles',
                key: amsAuthActive ? PATH.ADMIN_ROLES : PATH.TABLE_ROLES,
              } : null,
              canManageDataAccessGroups ? {
                label: 'Data Access Groups',
                key: PATH.DATA_ACCESS_GROUPS,
              } : null,
              canManageTags ? {
                label: 'Tags',
                key: PATH.TABLE_TAGS,
              } : null,
              canManageAging ? {
                label: 'Aging',
                key: PATH.CONFIGURE_AGING_RULES,
              } : null,
              canManageCompanyJobRewrites ? {
                label: 'Local Grades',
                key: PATH.COMPANY_JOBS_REWRITES,
              } : null,
              isTechSettingsMenuDisplayed ? {
                label: 'Tech Settings',
                key: techSettingsPagePath
              } : null,
              showWorkspaces ? {
                label: 'Workspaces',
                key: PATH.ADMIN_WORKSPACES,
              } : null,
            ]
          } : null,
          blueprintsEnabled ? {
            label: 'Blueprints',
            key: PATH.BLUEPRINTS,
          } : null,
        ].reduce(
          (acc: any, route: any) => {
            if (!route) {
              return acc;
            }
            if (!route.children) {
              return [...acc, route];
            }
            const children = route.children.filter((child: any) => !!child);

            return [...acc, { ...route, children }];
          }, [])
      )
    ),
    [
      blueprintsEnabled,
      showWorkspaces,
      showUsers,
      showRoles,
      amsAuthActive,
      canManageAging,
      canManageCompanyJobRewrites,
      canManageCompanyJobs,
      canManageCurrencies,
      canManageCustomPercentiles,
      canManageDataAccessGroups,
      canManageDataCutPaymarketMapping,
      canManageDataManagement,
      canViewDataModels,
      canManageEmployees,
      canManageLevelMappings,
      canManagePaymarkets,
      canManagePlans,
      canManagePricingPolicies,
      canManageReports,
      canManageSurveys,
      canManageTags,
      canManageTargetPercentiles,
      canViewMarketPricing,
      canViewAnalyzeDashboards,
      isMyDataMenuDisplayed,
      isSettingsMenuDisplayed,
      isTechSettingsMenuDisplayed,
      quickSightAuthoringEnabled,
      techSettingsPagePath,
    ],
  );
};
